import { memo, useCallback, useEffect, useState } from "react";
import { Pagination } from "services/search";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { NoResults } from "ui/components/NoResults";
import { TableSearch } from "ui/components/Table/TableSearch";
import { DASHBOARD_CONTRACT_RENT_SEARCH_COLUMNS } from "services/api/dashboard/consts";
import { transformRoute, useTranslate } from "helpers";
import {
  deleteFleetVehicleContractRentAPI,
  getFleetVehicleContractRentsAPI,
} from "services/api/fleet/vehicles/contracts/api";
import { FleetVehicleContractRentRow } from "services/api/fleet/vehicles/contracts/types";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import { Routes as ContactRoutes } from "ui/modules/contacts/navigation";
import { useNavigate } from "react-router-dom";
import { Dialog } from "ui/components/Dialog";

const ContractRentSearchTab: React.FC = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehicleContractRentRow>
  >(initialDataWithPagination);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [contractId, setContractId] = useState<string>("");
  const [contractVehicleId, setContractVehicleId] = useState<string>("");
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (pagination: Pagination) => {
    setIsLoading(true);
    try {
      const results = await getFleetVehicleContractRentsAPI(pagination);
      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback((id: string) => {
    const contract = searchResults.data.find((c) => c.id === id);
    const vehicleId = contract?.vehicle?.id || contract?.offerRent?.vehicle?.id;
    if (vehicleId) {
      navigate(
        transformRoute(FleetVehicleRoutes.FleetVehicleContractSearchPageRouter, {
          id: vehicleId,
        })
      );
    }
  }, [searchResults.data, navigate]);

  const handleDeleteClick = (
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    id: string,
    vehicleId: string
  ) => {
    event.stopPropagation();
    event.preventDefault();
    setContractId(id);
    setContractVehicleId(vehicleId);
    setOpenDialog(true);
  };

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!contractId || !contractVehicleId) return;
    try {
      const deleted = await deleteFleetVehicleContractRentAPI(contractVehicleId, contractId, t);
      if (deleted) {
        await fetchSearchResult(searchResults.pagination);
      }
      setOpenDialog(false);
      setContractId("");
      setContractVehicleId("");
    } catch (err) {
      console.error("Error deleting rent contract:", err);
    }
  }, [contractId, contractVehicleId, t, searchResults.pagination]);

  const handleDeleteDialogClose = useCallback(() => {
    setContractId("");
    setContractVehicleId("");
    setOpenDialog(false);
  }, []);

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  const handleContactClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, contactId: string) => {
      event.preventDefault();
      event.stopPropagation();
      navigate(transformRoute(ContactRoutes.ContactDetailPageRouter, { id: contactId }));
    },
    [navigate]
  );

  return (
    <div className="rounded-md mb-4 h-full w-full">
      <div>
        {!isLoading && searchResults.pagination.totalPages === 0 ? (
          <NoResults message={t("admin.dashboard.widget.contracts.search.noresults")} />
        ) : (
          <div className="mx-[-1px] mb-[-1px]">
            <TableSearch
              columns={DASHBOARD_CONTRACT_RENT_SEARCH_COLUMNS(t, handleContactClick, handleDeleteClick)}
              data={searchResults.data}
              selectedRows={[]}
              pagination={
                searchResults.pagination || initialDataWithPagination.pagination
              }
              selectableRows={false}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>
      <Dialog
        open={openDialog}
        title={t("admin.fleet.vehicle.contract.dialog.delete.title")}
        message={t("admin.fleet.vehicle.contract.dialog.delete.message")}
        confirmText={t("admin.fleet.vehicle.contract.dialog.delete.confirmtext")}
        cancelText={t("admin.fleet.vehicle.contract.dialog.delete.canceltext")}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </div>
  );
};

export default memo(ContractRentSearchTab);
